import { useCallback, useRef } from 'react';

import { useTheme } from '@mui/material';
import { ChartEventsOptions } from 'highcharts';
import Highcharts from 'highcharts/highstock';

import useBaseChartOptions from '@/components/Chart/hooks/options/useBaseChartOptions';
import getRegularTooltip from '@/components/Chart/hooks/tooltip/getRegularTooltip';
import useSpotTooltipFormatter from '@/components/Chart/hooks/tooltip/useSpotTooltipFormatter';
import { ExtendedOptions, ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';
import { createLastPriceLabel } from '@/components/Chart/utils/createLastPriceLabel';
import { DEFAULT_CHART_HEIGHT, TABLE_ROW_HEIGHT_PX } from '@/constants/constants';
import useUserDetails from '@/hooks/useUserDetails';

import useChartFunctions from './useChartFunctions';

const useRegularOptions = (height?: number, isDhChart = false) => {
  const createTooltipFormatter = useSpotTooltipFormatter(isDhChart ? 'Alias' : undefined);
  const theme = useTheme();
  const { tooltip } = getRegularTooltip(createTooltipFormatter, theme);
  const { isUserBasic, isUserExpired } = useUserDetails();

  const { createBaseChartOptions } = useBaseChartOptions(
    height ? height * TABLE_ROW_HEIGHT_PX + 40 : DEFAULT_CHART_HEIGHT
  );

  const { createEventOptions } = useChartFunctions();
  const watermarkImage = useRef<Highcharts.SVGElement | null>(null);

  const getWatermarkAttributes = (chart: Highcharts.Chart) =>
    isUserBasic || isUserExpired
      ? {
          // Centered large watermark for basic/expired users
          width: 190 * 3,
          height: 50 * 3,
          x: Math.round((chart.chartWidth - 190 * 3) / 2),
          y: Math.round((chart.chartHeight - 50 * 3) / 2),
          zIndex: 1
        }
      : {
          // Bottom-right watermark with fixed margins
          width: 190,
          height: 50,
          x: Math.max(0, chart.chartWidth - 250),
          y: Math.max(0, chart.chartHeight - 190),
          zIndex: 1
        };

  const events: ChartEventsOptions = createEventOptions(
    watermarkImage,
    getWatermarkAttributes,
    '/watermark-black.svg'
  );
  const createOptions = useCallback(
    (chartSeries: ExtendedSeriesOptionsType[]): ExtendedOptions => {
      let options = createBaseChartOptions(
        chartSeries,
        theme.palette.text.primary,
        theme.palette.background.paper
      );
      options = { ...options, chart: { ...options.chart, events } };
      options = {
        ...options,
        plotOptions: {
          ...options.plotOptions,
          series: {
            ...options.plotOptions?.series,
            dataLabels: {
              position: 'right',
              enabled: true,
              useHTML: true,
              formatter(): string | undefined {
                return createLastPriceLabel(this, theme.palette.background.default);
              }
            }
          }
        },
        exporting: {
          ...options.exporting,
          chartOptions: {
            ...options.exporting?.chartOptions,
            plotOptions: { series: { dataLabels: { enabled: false } } }
          },
          sourceWidth: undefined,
          sourceHeight: undefined,
          buttons: undefined
        }
      };
      options = {
        ...options,
        stockTools: {
          gui: {
            enabled: false
          }
        }
      };

      options = { ...options, tooltip };
      options = { ...options, series: chartSeries };
      return options;
    },
    [
      createBaseChartOptions,
      theme.palette.background.default,
      theme.palette.background.paper,
      theme.palette.text.primary,
      tooltip,
      events
    ]
  );

  return { createOptions };
};

export default useRegularOptions;
